import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { visibleSections } from '../nav';
import { useAttention } from '../attention';
import RangePicker from './RangePicker';
import CommandPalette from './CommandPalette';

/**
 * Five sections, one time range, one search box.
 *
 * The width matters and is a deliberate change: the old shell capped content
 * at `max-w-6xl`, which put the twelve-column product table into permanent
 * horizontal scroll on the page whose entire job was comparing those columns.
 * An internal ops tool is used on a desktop; give the tables the room.
 */
const navItem = 'rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors';
const navOn = 'bg-raspberry-50 text-raspberry-700';
const navOff = 'text-ink-secondary hover:bg-surface-page hover:text-ink';

function isMac() {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
}

export default function AppShell() {
  const { admin, signOut } = useAdminAuth();
  const { badges, items } = useAttention();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const sections = visibleSections(admin?.role);
  const attentionCount = items.length;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      // Bare "/" opens search too, but only when the user isn't already
      // typing — otherwise it eats the keystroke inside every filter input.
      if (e.key === '/' && !/^(INPUT|TEXTAREA)$/.test(e.target?.tagName)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const sectionBadge = (key) => {
    if (key === 'catalog') return (badges.pendingPartners || 0) + (badges.failedCrawls || 0);
    if (key === 'home') return attentionCount;
    return 0;
  };

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="sticky top-0 z-20 border-b border-hairline bg-surface">
        <div className="mx-auto flex h-14 max-w-[1440px] items-center justify-between gap-4 px-6">
          <div className="flex min-w-0 items-center gap-6">
            <span className="flex-shrink-0 text-base font-semibold tracking-tight text-ink">
              admin<span className="text-raspberry-600">.mahi</span>
            </span>
            <nav className="flex items-center gap-1" aria-label="Sections">
              {sections.map((s) => {
                const count = sectionBadge(s.key);
                return (
                  <NavLink
                    key={s.key}
                    to={s.path}
                    end={s.end}
                    className={({ isActive }) => `${navItem} ${isActive ? navOn : navOff}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {s.label}
                      {count > 0 && (
                        <span className="rounded-full bg-status-warning/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-[#8a5a00]">
                          {count}
                        </span>
                      )}
                    </span>
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-hairline bg-surface px-2.5 py-1.5 text-xs text-ink-muted hover:bg-surface-page hover:text-ink"
              aria-label="Search"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Search
              <kbd className="rounded border border-hairline px-1 text-[10px] font-medium">
                {isMac() ? '⌘' : 'Ctrl '}K
              </kbd>
            </button>

            <RangePicker />

            <div className="ml-1 flex items-center gap-2 border-l border-hairline pl-3">
              <div className="text-right leading-tight">
                <div className="text-xs font-medium text-ink">{admin?.name}</div>
                <div className="text-[11px] capitalize text-ink-muted">{admin?.role}</div>
              </div>
              <button
                onClick={signOut}
                className="rounded-lg border border-hairline px-2 py-1.5 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 py-7">
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
